import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form, Badge, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

const PLATFORMS = [
  { value: 'linkedin', label: 'LinkedIn', color: '#0077B5' },
  { value: 'twitter', label: 'Twitter/X', color: '#000000' },
  { value: 'youtube', label: 'YouTube', color: '#FF0000' },
  { value: 'blog', label: 'Blog', color: colors.goldDark },
  { value: 'podcast', label: 'Podcast', color: '#9B59B6' },
  { value: 'other', label: 'Other', color: colors.greyMid },
];

function MarketIntelligence() {
  const navigate = useNavigate();

  // Thought leaders state
  const [leaders, setLeaders] = useState([]);
  const [leadersLoading, setLeadersLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLeader, setEditingLeader] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    platform: 'linkedin',
    platform_url: '',
    focus_area: '',
    why_relevant: '',
  });

  const fetchLeaders = useCallback(async () => {
    try {
      const res = await fetch('/api/industry-pulse/thought-leaders', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setLeaders(data.thought_leaders || []);
      }
    } catch (err) {
      console.error('Failed to fetch thought leaders:', err);
    } finally {
      setLeadersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeaders();
  }, [fetchLeaders]);

  const openAddModal = () => {
    setEditingLeader(null);
    setFormData({ name: '', platform: 'linkedin', platform_url: '', focus_area: '' });
    setShowAddModal(true);
  };

  const openEditModal = (leader) => {
    setEditingLeader(leader);
    setFormData({
      name: leader.name,
      platform: leader.platform || 'linkedin',
      platform_url: leader.platform_url || '',
      focus_area: leader.focus_area || '',
    });
    setShowAddModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return;
    setSaving(true);
    try {
      const url = editingLeader
        ? `/api/industry-pulse/thought-leaders/${editingLeader.id}`
        : '/api/industry-pulse/thought-leaders';
      const res = await fetch(url, {
        method: editingLeader ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setShowAddModal(false);
        fetchLeaders();
      } else {
        const errData = await res.json().catch(() => ({}));
        alert(errData.error || 'Failed to save. Is the backend running?');
      }
    } catch (err) {
      console.error('Failed to save thought leader:', err);
      alert('Could not connect to the server. Please restart the backend.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this thought leader?')) return;
    try {
      const res = await fetch(`/api/industry-pulse/thought-leaders/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        setLeaders(prev => prev.filter(l => l.id !== id));
      }
    } catch (err) {
      console.error('Failed to delete thought leader:', err);
    }
  };

  const getPlatform = (value) => PLATFORMS.find(p => p.value === value) || PLATFORMS[5];

  const cards = [
    {
      title: 'Customer List',
      description: 'View your customers, their lifetime value, contact info, and purchase history.',
      path: '/command-center/market-intelligence/customer-list',
      buttonText: 'View Customers',
    },
    {
      title: 'Customer Intelligence',
      description: 'Behavioral insights, churn risk, and your Intelligence Score \u2014 the data asset that commands premium exit multiples.',
      path: '/command-center/market-intelligence/customer-intelligence',
      buttonText: 'View Intelligence',
      comingSoon: true,
    },
    {
      title: 'Industry Pulse',
      description: 'Trending topics, thought leaders, and what\'s happening in your market. Powers your content generation with real-time intelligence.',
      path: '/command-center/market-intelligence/industry-pulse',
      buttonText: 'View Industry Pulse',
    },
  ];

  return (
    <Container className="py-4">
      <h1 className="mb-2" style={{
        fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif",
        letterSpacing: '0.15em',
      }}>
        Market Intelligence
      </h1>
      <p className="text-muted mb-4">
        Your radar — watch your customers and your market from one place.
      </p>

      <Row>
        {cards.map((card) => (
          <Col md={6} key={card.title} className="mb-4">
            <Card
              className="h-100 shadow-sm"
              style={{
                border: `1px solid ${colors.greyLight}`,
                cursor: card.comingSoon ? 'default' : 'pointer',
                transition: 'all 0.2s ease',
              }}
              onClick={() => !card.comingSoon && navigate(card.path)}
              onMouseEnter={(e) => {
                if (!card.comingSoon) {
                  e.currentTarget.style.borderColor = colors.goldMid;
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = colors.greyLight;
                e.currentTarget.style.boxShadow = '0 .125rem .25rem rgba(0,0,0,.075)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <Card.Header style={{
                backgroundColor: colors.greyDarkest,
                color: colors.goldLight,
                fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif",
                letterSpacing: '0.1em',
              }}>
                <strong>{card.title}</strong>
              </Card.Header>
              <Card.Body className="d-flex flex-column p-4">
                <p className="text-muted flex-grow-1">{card.description}</p>
                <Button
                  variant="outline-dark"
                  className="mt-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!card.comingSoon) navigate(card.path);
                  }}
                  disabled={card.comingSoon}
                >
                  {card.comingSoon ? 'Coming Soon' : `${card.buttonText} \u2192`}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Thought Leaders Section */}
      <Card className="shadow-sm mt-2">
        <Card.Header style={{
          backgroundColor: colors.greyDarkest,
          color: colors.goldLight,
          fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif",
          letterSpacing: '0.1em',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <strong>Your Industry Thought Leaders</strong>
          <Button
            size="sm"
            variant="outline-light"
            onClick={openAddModal}
            style={{ borderColor: colors.goldLight, color: colors.goldLight }}
          >
            + Add Thought Leader
          </Button>
        </Card.Header>
        <Card.Body className="p-4">
          {leadersLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" style={{ color: colors.goldMid }} />
            </div>
          ) : leaders.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted mb-3">
                Add the thought leaders and influencers in your space. Their content informs your topic suggestions and keeps you current.
              </p>
              <Button variant="outline-dark" onClick={openAddModal}>
                Add Your First Thought Leader
              </Button>
            </div>
          ) : (
            <Row>
              {leaders.map((leader) => {
                const plat = getPlatform(leader.platform);
                return (
                  <Col md={4} key={leader.id} className="mb-3">
                    <Card className="h-100" style={{ border: `1px solid ${colors.greyLight}` }}>
                      <Card.Body className="p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <strong style={{ color: colors.greyDarkest }}>{leader.name}</strong>
                            <div>
                              <Badge
                                style={{
                                  backgroundColor: plat.color,
                                  color: '#fff',
                                  fontSize: '0.7rem',
                                  fontWeight: 500,
                                }}
                              >
                                {plat.label}
                              </Badge>
                            </div>
                          </div>
                          <div className="d-flex gap-1">
                            {leader.platform_url && (
                              <Button
                                size="sm"
                                variant="link"
                                className="p-0"
                                href={leader.platform_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Visit profile"
                                as="a"
                              >
                                &#128279;
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="link"
                              className="p-0 ms-1"
                              onClick={() => openEditModal(leader)}
                              title="Edit"
                              style={{ color: colors.greyMid }}
                            >
                              &#9998;
                            </Button>
                            <Button
                              size="sm"
                              variant="link"
                              className="p-0 ms-1"
                              onClick={() => handleDelete(leader.id)}
                              title="Remove"
                              style={{ color: '#dc3545' }}
                            >
                              &times;
                            </Button>
                          </div>
                        </div>
                        {leader.focus_area && (
                          <small className="text-muted d-block">{leader.focus_area}</small>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Add/Edit Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header
          closeButton
          style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight, border: 'none' }}
        >
          <Modal.Title style={{
            fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif",
            letterSpacing: '0.1em',
            fontSize: '1.1rem',
          }}>
            {editingLeader ? 'Edit Thought Leader' : 'Add Thought Leader'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#f4f4f4' }}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Alex Hormozi"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Primary Platform</Form.Label>
              <Form.Select
                value={formData.platform}
                onChange={(e) => setFormData(prev => ({ ...prev, platform: e.target.value }))}
              >
                {PLATFORMS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Profile/Content URL</Form.Label>
              <Form.Control
                type="url"
                placeholder="https://..."
                value={formData.platform_url}
                onChange={(e) => setFormData(prev => ({ ...prev, platform_url: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>What they're known for <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. SaaS pricing strategy, PLG growth"
                value={formData.focus_area}
                onChange={(e) => setFormData(prev => ({ ...prev, focus_area: e.target.value }))}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#f4f4f4', border: 'none' }}>
          <Button variant="outline-secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button
            variant="dark"
            onClick={handleSave}
            disabled={saving || !formData.name.trim()}
            style={{ backgroundColor: colors.greyDarkest }}
          >
            {saving ? 'Saving...' : (editingLeader ? 'Save Changes' : 'Add Thought Leader')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default MarketIntelligence;
